import { Component, OnInit, Inject, Optional } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { MatDialog, MatDialogConfig, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MessageService } from 'primeng/api';
import { first } from 'rxjs/operators';
import { TrackedLinkService } from '@app/_components/tracked-link';
import { ReservationService } from '@app/_components/reservation/reservation.service';
import { MsgService } from '@app/_components/msg';
import { InboxComponent } from '@app/_components/inbox/inbox/inbox.component';

@Component({
	standalone: false,
	selector: 'app-campaign-analytics',
	templateUrl: './campaign-analytics.component.html',
	styleUrls: ['./campaign-analytics.component.css'],
	providers: [MessageService]
})
export class CampaignAnalyticsComponent implements OnInit {

	campaigns: any[] = [];
	campaignId: number = null;
	dateFrom: string = new Date().toISOString().substring(0, 10);
	dateTo: string = new Date().toISOString().substring(0, 10);
	stats: any = null;
	loading = false;
	campaignsLoading = true;

	// Recipient detail
	recipients: any[] = [];
	recipientLoading = false;
	recipientType: string = null; // 'opened' or 'clicked'
	recipientPage: number = 0;
	recipientPageSize: number = 25;

	// Recipient table sorting
	recipientSortField: string = null;
	recipientSortDir: number = 1; // 1 = asc, -1 = desc

	// URL breakdown
	urlClicks: any[] = [];
	urlLoading = false;

	// Topic map
	topicMap: { [key: number]: string } = {
		100: 'Reservation', 200: 'Campaign', 300: 'IMAP Read', 400: 'Jobs',
		500: 'Owner Profile', 600: 'Task', 700: 'Proforma', 800: 'Website Query',
		900: 'Custom Mail', 1000: 'Public Review', 1100: 'Sent Review Request',
		1200: 'Survey Guest Exit', 1300: 'Created Lead Profile', 1600: 'SMS Received',
		1700: 'Send SMS', 1800: 'Review Notification'
	};

	// Track loading state per reservationId for open order
	openingOrder: { [key: number]: boolean } = {};
	isPage = false;

	constructor(
		private trackedLinkService: TrackedLinkService,
		private reservationService: ReservationService,
		private messageService: MessageService,
		private dialog: MatDialog,
		private msgService: MsgService,
		private route: ActivatedRoute,
		@Optional() @Inject(MAT_DIALOG_DATA) data: any
	) {
		if (data && data.campaignId) {
			this.campaignId = data.campaignId;
		}
		if (data && data.dateFrom !== undefined) {
			this.dateFrom = data.dateFrom;
		}
		if (data && data.dateTo !== undefined) {
			this.dateTo = data.dateTo;
		}
		this.isPage = !data;
	}

	ngOnInit() {
		if (this.isPage) {
			const urlParams = new URLSearchParams(window.location.search);
			const campaignId = urlParams.get('campaignId');
			if (campaignId) { this.campaignId = +campaignId; }
			const dateFrom = urlParams.get('dateFrom');
			if (dateFrom !== null) { this.dateFrom = dateFrom; }
			const dateTo = urlParams.get('dateTo');
			if (dateTo !== null) { this.dateTo = dateTo; }
		}
		this.loadCampaigns();
	}

	loadCampaigns() {
		this.campaignsLoading = true;
		this.trackedLinkService.getCampaigns().pipe(first()).subscribe(
			(res: any) => {
				this.campaigns = (res.data.campaigns || []).map((c: any) => ({
					...c,
					displayLabel: c.msgCampaignId + ' - ' + (c.name || '')
				}));
				// Auto-search if campaignId was pre-selected
				if (this.campaignId) { this.search(); }
			},
			(err: any) => { console.log('Campaign load error: ' + JSON.stringify(err)); },
			() => { this.campaignsLoading = false; }
		);
	}

	search() {
		if (!this.campaignId) { return; }
		this.loading = true;
		this.stats = null;
		this.recipients = [];
		this.recipientType = null;
		this.urlClicks = [];
		this.trackedLinkService.getCampaignAnalytics(
			this.campaignId,
			this.dateFrom || undefined,
			this.dateTo || undefined
		).pipe(first()).subscribe(
			(res: any) => {
				this.stats = res.data.analytics;
				// Auto-load URL breakdown for campaigns
				this.loadUrlClicks();
			},
			(err: any) => {
				console.log('Campaign analytics error: ' + JSON.stringify(err));
				this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load campaign analytics.', life: 3000 });
			},
			() => { this.loading = false; }
		);
	}

	loadRecipients(type: string) {
		if (!this.campaignId) { return; }
		this.recipientType = type;
		this.recipientLoading = true;
		this.recipients = [];
		this.recipientPage = 0;

		let obs;
		if (type === 'all') {
			obs = this.trackedLinkService.getCampaignAllRecipients(this.campaignId, this.dateFrom || undefined, this.dateTo || undefined);
		} else if (type === 'opened') {
			obs = this.trackedLinkService.getCampaignOpenedRecipients(this.campaignId, this.dateFrom || undefined, this.dateTo || undefined);
		} else {
			obs = this.trackedLinkService.getCampaignClickedRecipients(this.campaignId, this.dateFrom || undefined, this.dateTo || undefined);
		}

		obs.pipe(first()).subscribe(
			(res: any) => { this.recipients = res.data.recipients || []; },
			(err: any) => {
				console.log('Recipient load error: ' + JSON.stringify(err));
				this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load recipients.', life: 3000 });
			},
			() => { this.recipientLoading = false; }
		);
	}

	loadUrlClicks() {
		if (!this.campaignId) { return; }
		this.urlLoading = true;
		this.urlClicks = [];
		this.trackedLinkService.getCampaignUrlClicks(
			this.campaignId, this.dateFrom || undefined, this.dateTo || undefined
		).pipe(first()).subscribe(
			(res: any) => { this.urlClicks = res.data.urlClicks || []; },
			(err: any) => { console.log('URL clicks error: ' + JSON.stringify(err)); },
			() => { this.urlLoading = false; }
		);
	}

	sortRecipients(field: string) {
		if (this.recipientSortField === field) {
			this.recipientSortDir = this.recipientSortDir * -1;
		} else {
			this.recipientSortField = field;
			this.recipientSortDir = 1;
		}
		this.recipients = [...this.recipients].sort((a, b) => {
			const aVal = a[field] ?? '';
			const bVal = b[field] ?? '';
			if (aVal < bVal) { return -1 * this.recipientSortDir; }
			if (aVal > bVal) { return 1 * this.recipientSortDir; }
			return 0;
		});
		this.recipientPage = 0;
	}

	getSortIcon(field: string): string {
		if (this.recipientSortField !== field) { return 'pi pi-sort-alt'; }
		return this.recipientSortDir === 1 ? 'pi pi-sort-amount-up-alt' : 'pi pi-sort-amount-down-alt';
	}

	get pagedRecipients(): any[] {
		const start = this.recipientPage * this.recipientPageSize;
		return this.recipients.slice(start, start + this.recipientPageSize);
	}

	onRecipientPageChange(event: any) {
		this.recipientPage = event.page;
		this.recipientPageSize = event.rows;
	}

	openMessage(msgId: number) {
		if (!msgId) { return; }
		this.msgService.findById(msgId).pipe(first()).subscribe(
			(res: any) => {
				const msg = res.data.msg;
				if (!msg) {
					this.messageService.add({ severity: 'warn', summary: 'Not Found', detail: 'Message ' + msgId + ' not found.', life: 3000 });
					return;
				}
				const dialogConfig = new MatDialogConfig();
				dialogConfig.width = '100%';
				dialogConfig.height = '100%';
				dialogConfig.maxWidth = '1600px';
				dialogConfig.maxHeight = '800px';
				dialogConfig.disableClose = false;
				dialogConfig.autoFocus = true;
				dialogConfig.hasBackdrop = true;
				dialogConfig.data = { inbox: msg, title: 'View Message' };
				this.dialog.open(InboxComponent, dialogConfig);
			},
			(err: any) => {
				this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load message ' + msgId, life: 3000 });
			}
		);
	}

	openOrder(reservationId: number) {
		if (!reservationId || this.openingOrder[reservationId]) { return; }
		this.openingOrder[reservationId] = true;
		this.reservationService.getOrderIdByReservationId(reservationId).pipe(first()).subscribe(
			(res: any) => {
				window.open(`https://admin.kbmhawaii.com/order/${res?.data?.orderId}`, '_blank');
				this.openingOrder[reservationId] = false;
			},
			() => {
				this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to fetch order for reservation ' + reservationId, life: 3000 });
				this.openingOrder[reservationId] = false;
			}
		);
	}

	getTopicName(topic: number): string {
		if (!topic) return '';
		return this.topicMap[topic] || String(topic);
	}

	get openRate(): number {
		if (!this.stats || !this.stats.totalSent || this.stats.totalSent === 0) { return 0; }
		return (this.stats.totalOpened / this.stats.totalSent) * 100;
	}

	get clickRate(): number {
		if (!this.stats || !this.stats.totalSent || this.stats.totalSent === 0) { return 0; }
		return (this.stats.totalClicked_Recipients / this.stats.totalSent) * 100;
	}

	openInNewTab() {
		const params = new URLSearchParams();
		if (this.campaignId) { params.set('campaignId', String(this.campaignId)); }
		if (this.dateFrom) { params.set('dateFrom', this.dateFrom); }
		if (this.dateTo) { params.set('dateTo', this.dateTo); }
		window.open('/campaign-analytics?' + params.toString(), '_blank');
	}

	getSelectedCampaignName(): string {
		if (!this.campaignId || !this.campaigns) { return ''; }
		const found = this.campaigns.find(c => c.msgCampaignId === this.campaignId);
		return found ? found.name : '';
	}
}
